import { getLimitedEventsByTypeGroup } from 'shared-helpers/common';

import type {
  EventLogType,
  EventLogTypeGroup,
  ServerConfig
} from 'shared-types';
import type { ReduxRootState } from 'types';

interface Params {
  messages: ReduxRootState['messages'];
  onlyEventsOfType?: EventLogTypeGroup;
  serverConfig: Pick<
    ServerConfig,
    | 'hideAchievementsFromUI'
    | 'hideCalendarEventsFromUI'
    | 'hideCoursesFromUI'
    | 'hidePathsFromUI'
  >;
}

const getEventLogTypes = ({
  messages,
  onlyEventsOfType,
  serverConfig
}: Params) => {
  const events = {
    all: messages.commonShowAll,
    'accept-individual-achievement-request':
      messages.activitiesTitleUpdateIndividualAchievement,
    'achievement-completion-closing-approval':
      messages.activitiesTitleAchievementCompletionClosingApproval,
    'achievement-completion-conversation-closed':
      messages.activitiesTitleAchievementCompletionConversationClosed,
    'activate-two-factor-authentication':
      messages.activitiesTitleActivateTwoFactorAuthentication,
    'activate-shared-two-factor-authentication':
      messages.activitiesTitleActivateSharedTwoFactorAuthentication,
    'achievement-access-denied': messages.activitiesTitleAccessRequestDenied,
    'add-admin-group-user': messages.activitiesTitleAddAdminGroupUser,
    'add-achievement-admin': messages.activitiesTitleAddAchievementAdmin,
    'add-achievement-owner': messages.activitiesTitleAddAchievementOwner,
    'add-achievement-completions': messages.activitiesTitleAddAchievementUsers,
    'allow-new-course-completion-attempt':
      messages.activitiesTitleAllowNewCourseCompletionAttempt,
    'add-calendar-event-attachment':
      messages.activitiesTitleAddCalendarEventAttachment,
    'add-calendar-event-feedback-question':
      messages.activitiesTitleAddCalendarEventFeedbackQuestion,
    'add-calendar-event-feedback-template':
      messages.activitiesTitleAddCalendarEventFeedbackTemplate,
    'add-calendar-event-feedback-template-question':
      messages.activitiesTitleAddCalendarEventFeedbackTemplateQuestion,
    'add-calendar-event-owner': messages.activitiesTitleAddCalendarEventOwner,
    'add-calendar-event-participation':
      messages.activitiesTitleAddCalendarEventParticipation,
    'add-calendar-event-self': messages.activitiesTitleAddCalendarEventSelf,
    'add-calendar-event-users': messages.activitiesTitleAddCalendarEventUser,
    'apply-calendar-event-feedback-template':
      messages.activitiesTitleApplyCalendarEventFeedbackTemplate,
    'add-client-page-section': messages.activitiesTitleAddClientPageSection,
    'add-client-page-section-attachment':
      messages.activitiesTitleAddClientPageSectionAttachment,
    'add-competency-admin': messages.activitiesTitleAddCompetencyAdmin,
    'add-competency-endorsement':
      messages.activitiesTitleAddCompetencyEndorsement,
    'add-competency-profile-admin':
      messages.activitiesTitleAddCompetencyProfileAdmin,
    'add-course-admin': messages.activitiesTitleAddCourseAdmin,
    'add-course-owner': messages.activitiesTitleAddCourseOwner,
    'add-course-users': messages.activitiesTitleAddCourseUsers,
    'add-course-language-version':
      messages.activitiesTitleAddCourseLanguageVersion,
    'add-email-message-template-product':
      messages.activitiesTitleAddEmailMessageTemplateProduct,
    'add-group-admin': messages.activitiesTitleAddGroupAdmin,
    'add-group-course': messages.activitiesTitleAddGroupCourse,
    'add-group-owner': messages.activitiesTitleAddGroupOwner,
    'add-group-to-supervisor': messages.activitiesTitleAddGroupToSupervisor,
    'add-group-users': messages.activitiesTitleAddGroupUsers,
    'add-path-group': messages.activitiesTitleAddPathGroup,
    'add-path-owner': messages.activitiesTitleAddPathOwner,
    'add-path-step': messages.activitiesTitleAddPathStep,
    'add-path-users': messages.activitiesTitleAddPathUsers,
    'add-supervisees': messages.activitiesTitleAddSuperviseesToSupervisor,
    'add-user-competencies': messages.activiesTitleAddUserCompetencies,
    'change-supervisor': messages.activitiesTitleChangeSupervisor,
    'competency-access-denied': messages.activitiesTitleAccessRequestDenied,
    'complete-course': messages.activitiesTitleCompleteCourse,
    'complete-course-failed': messages.activitiesTitleCompleteCourseFailed,
    'complete-course-waiting': messages.activitiesTitleCompleteCourseWaiting,
    'complete-path': messages.activitiesTitleCompletePath,
    'copy-calendar-event': messages.activitiesTitleCopyCalendarEvent,
    'copy-chapter': messages.activitiesTitleCopyChapter,
    'copy-competency': messages.activitiesTitleCopyCompetency,
    'copy-course': messages.activitiesTitleCopyCourse,
    'copy-path': messages.activitiesTitleCopyPath,
    'course-access-denied': messages.activitiesTitleAccessRequestDenied,
    'calendar-event-access-denied': messages.activitiesTitleAccessRequestDenied,
    'create-admin-group': messages.activitiesTitleCreateAdminGroup,
    'create-achievement-product-user-custom-attribute':
      messages.activitiesTitleCreateProductUserCustomAttribute,
    'create-answer': messages.activitiesTitleCreateAnswer,
    'create-answer-review': messages.activitiesTitleCreateAnswerReview,
    'create-calendar-event': messages.activitiesTitleCreateCalendarEvent,
    'create-calendar-event-product-user-custom-attribute':
      messages.activitiesTitleCreateProductUserCustomAttribute,
    'create-chapter': messages.activitiesTitleCreateChapter,
    'create-chat-message': messages.activitiesTitleCreateChatMessage,
    'create-competency': messages.activitiesTitleCreateCompetency,
    'create-competency-profile':
      messages.activitiesTitleCreateCompetencyProfile,
    'create-course': messages.activitiesTitleCreateCourse,
    'create-discussion-comment':
      messages.activitiesTitleCreateDiscussionComment,
    'create-achievement': messages.activitiesTitleCreateAchievement,
    'create-auth-strategy': 'Created auth strategy',
    'create-email-message-template':
      messages.activitiesTitleCreateEmailMessageTemplate,
    'create-feedback': messages.activitiesTitleCreateFeedback,
    'create-group': messages.activitiesTitleCreateGroup,
    'create-h5p-status': messages.activitiesTitleCreateH5pStatus,
    'create-individual-achievement':
      messages.activitiesTitleCreateIndividualAchievement,
    'create-integration': 'Created integration',
    'create-path': messages.activitiesTitleCreatePath,
    'create-scorm-status': messages.activitiesTitleCreateScormStatus,
    'create-supervisor': messages.activitiesTitleCreateSupervisor,
    'create-user': messages.activitiesTitleCreateUser,
    'delete-account': messages.activitiesTitleDeleteAccount,
    'delete-admin-group': messages.activitiesTitleDeleteAdminGroup,
    'delete-all-admin-groups': messages.activitiesTitleDeleteAdminGroupAll,
    'delete-answer': messages.activitiesTitleDeleteAnswer,
    'delete-answer-review': messages.activitiesTitleDeleteAnswerReview,
    'delete-calendar-event': messages.activitiesTitleDeleteCalendarEvent,
    'delete-chapter': messages.activitiesTitleDeleteChapter,
    'delete-chat-messages': messages.activitiesTitleDeleteChatMessages,
    'delete-client-page-section':
      messages.activitiesTitleDeleteClientPageSection,
    'delete-competency': messages.activitiesTitleDeleteCompetency,
    'delete-competency-profile':
      messages.activitiesTitleDeleteCompetencyProfile,
    'delete-all-competencies': messages.activitiesTitleDeleteCompetencyAll,
    'delete-all-competency-profiles':
      messages.activitiesTitleDeleteCompetencyProfileAll,
    'delete-auth-strategy': 'Deleted auth strategy',
    'delete-competency-endorsement':
      messages.activitiesTitleDeleteCompetencyEndorsement,
    'delete-course': messages.activitiesTitleDeleteCourse,
    'delete-course-data': messages.activitiesTitleDeleteCourseData,
    'delete-course-language-version':
      messages.activitiesTitleDeleteCourseLanguageVersion,
    'delete-discussion-comment':
      messages.activitiesTitleDeleteDiscussionComment,
    'delete-email-message-template':
      messages.activitiesTitleDeleteEmailMessageTemplate,
    'delete-expired-user': messages.activitiesTitleDeleteExpiredUser,
    'delete-achievement': messages.activitiesTitleDeleteAchievement,
    'delete-feedback': messages.activitiesTitleDeleteFeedback,
    'delete-group': messages.activitiesTitleDeleteGroup,
    'delete-individual-achievement':
      messages.activitiesTitleDeleteIndividualAchievement,
    'delete-integration': 'Deleted integration',
    'delete-path': messages.activitiesTitleDeletePath,
    'delete-path-completion': messages.activitiesTitleDeletePathCompletion,
    'delete-supervisor': messages.activitiesTitleDeleteSupervisor,
    'delete-user': messages.activitiesTitleDeleteUser,
    'delete-user-course-analytics':
      messages.activitiesTitleDeleteUserCourseAnalytics,
    'deny-individual-achievement-request':
      messages.achievementsIndividualDeniedRequest,
    'disable-two-factor-authentication':
      messages.activitiesTitleDisableTwoFactorAuthentication,
    'disable-shared-two-factor-authentication':
      messages.activitiesTitleDisableSharedTwoFactorAuthentication,
    'discard-chapter-changes': messages.activitiesTitleDiscardChapterChanged,
    'external-add-course-user': `${messages.activitiesTitleExternalAddCourseUser} ${messages.activitiesTextViaApi}`,
    'external-add-group-user': `${messages.activitiesTitleExternalAddGroupUser} ${messages.activitiesTextViaApi}`,
    'external-add-path-user': `${messages.activitiesTitleExternalAddPathUser} ${messages.activitiesTextViaApi}`,
    'external-add-user': `${messages.activitiesTitleExternalAddUser} ${messages.activitiesTextViaApi}`,
    'external-copy-course': `${messages.activitiesTitleExternalCopyCourse} ${messages.activitiesTextViaApi}`,
    'external-delete-user': `${messages.activitiesTitleExternalDeleteUser} ${messages.activitiesTextViaApi}`,
    'external-mycashflow-order-event':
      messages.activitiesTitleExternalMyCashflowOrderEvent,
    'external-shopify-order-event':
      messages.activitiesTitleExternalShopifyOrderEvent,
    'external-woocommerce-order-event':
      messages.activitiesTitleExternalWooCommerceOrderEvent,
    'external-update-user': `${messages.activitiesTitleUpdateUser} ${messages.activitiesTextViaApi}`,
    'fail-background-job': messages.activitiesTitleFailBackgroundJob,
    'generate-ai-translations': messages.activitiesTitleGenerateAITranslations,
    'generate-two-factor-backup-codes':
      messages.activitiesTitleGenerateTwoFactorBackupCodes,
    'hubspot-sync-contacts': `Hubspot ${messages.integrationsSyncContacts}`,
    'import-achievement': messages.activitiesTitleImportAchievement,
    'import-achievement-completions':
      messages.activitiesTitleImportAchievementCompletions,
    'import-achievement-template':
      messages.activitiesTitleImportAchievementTemplate,
    'import-course-language-version-json':
      messages.activitiesTitleImportCourseLanguageVersionJson,
    'join-course-with-link': messages.activitiesTitleJoinCourseWithLink,
    'join-path-with-link': messages.activitiesTitleJoinPathWithLink,
    'link-achievement-to-calendar-event':
      messages.activitiesTitleLinkAchievementToCalendarEvent,
    'manual-remove-course-completion':
      messages.activitiesTitleManualRemoveCourseCompletion,
    'manual-set-course-completion':
      messages.activitiesTitleManualSetCourseCompletion,
    'merge-users': messages.activitiesTitleMergeUsers,
    'pin-competency-profile': messages.activitiesTitlePinCompetencyProfile,
    'publish-chapter': messages.activitiesTitlePublishChapter,
    'remove-achievement-admin': messages.activitiesTitleRemoveAchievementAdmin,
    'remove-achievement-all-product-user-custom-attribute':
      messages.activitiesTitleRemoveProductUserCustomAttributeAll,
    'remove-achievement-completions':
      messages.activitiesTitleRemoveAchievementUsers,
    'remove-achievement-owner': messages.activitiesTitleRemoveAchievementOwner,
    'remove-achievement-product-user-custom-attribute':
      messages.activitiesTitleRemoveProductUserCustomAttribute,
    'remove-achievement-users-attachment':
      messages.activitiesTitleRemoveAchievementUsersAttachment,
    'remove-admin-group-user': messages.activitiesTitleRemoveAdminGroupUser,
    'remove-all-admin-group-users':
      messages.activitiesTitleRemoveAdminGroupUserAll,
    'remove-all-supervisee':
      messages.activitiesTitleRemoveSuperviseeFromSupervisorAll,
    'remove-all-calendar-event-feedback-questions':
      messages.activitiesTitleRemoveAllCalendarEventFeedbackQuestions,
    'remove-all-calendar-event-feedback-template-questions':
      messages.activitiesTitleRemoveAllCalendarEventFeedbackTemplateQuestions,
    'remove-calendar-event-attachment':
      messages.activitiesTitleRemoveCalendarEventAttachment,
    'remove-calendar-event-all-product-user-custom-attribute':
      messages.activitiesTitleRemoveProductUserCustomAttributeAll,
    'remove-calendar-event-feedback-question':
      messages.activitiesTitleRemoveCalendarEventFeedbackQuestion,
    'remove-calendar-event-feedback-submissions':
      messages.activitiesTitleRemoveCalendarEventFeedbackSubmissions,
    'remove-calendar-event-feedback-template':
      messages.activitiesTitleRemoveCalendarEventFeedbackTemplate,
    'remove-calendar-event-feedback-template-question':
      messages.activitiesTitleRemoveCalendarEventFeedbackTemplateQuestion,
    'remove-calendar-event-owner':
      messages.activitiesTitleRemoveCalendarEventOwner,
    'remove-calendar-event-participation':
      messages.activitiesTitleRemoveCalendarEventParticipation,
    'remove-calendar-event-product-user-custom-attribute':
      messages.activitiesTitleRemoveProductUserCustomAttribute,
    'remove-calendar-event-self':
      messages.activitiesTitleRemoveCalendarEventSelf,
    'remove-calendar-event-users':
      messages.activitiesTitleRemoveCalendarEventUsers,
    'remove-client-page-section-attachment':
      messages.activitiesTitleRemoveClientPageSectionAttachment,
    'remove-competency-admin': messages.activitiesTitleRemoveCompetencyAdmin,
    'remove-competency-profile-admin':
      messages.activitiesTitleRemoveCompetencyProfileAdmin,
    'remove-course-admin': messages.activitiesTitleRemoveCourseAdmin,
    'remove-course-owner': messages.activitiesTitleRemoveCourseOwner,
    'remove-course-users': messages.activitiesTitleRemoveCourseUsers,
    'remove-email-message-template-product':
      messages.activitiesTitleRemoveEmailMessageTemplateProduct,
    'remove-group-admin': messages.activitiesTitleRemoveGroupAdmin,
    'remove-group-course': messages.activitiesTitleRemoveGroupCourse,
    'remove-group-owner': messages.activitiesTitleRemoveGroupOwner,
    'remove-group-from-supervisor':
      messages.activitiesTitleRemoveGroupFromSupervisor,
    'remove-group-user': messages.activitiesTitleRemoveGroupUser,
    'remove-path-group': messages.activitiesTitleRemovePathGroup,
    'remove-path-owner': messages.activitiesTitleRemovePathOwner,
    'remove-path-step': messages.activitiesTitleRemovePathStep,
    'remove-path-users': messages.activitiesTitleRemovePathUsers,
    'remove-supervisee': messages.activitiesTitleRemoveSuperviseeFromSupervisor,
    'remove-user-competency': messages.activitiesTitleRemoveUserCompetency,
    'request-individual-achievement':
      messages.activitiesTitleRequestIndividualAchievement,
    'retry-failed-course-completion':
      messages.activitiesTitleRetryFailedCourseCompletion,
    'revert-chapter-version': messages.activitiesTitleRevertedChapterVersion,
    'revert-course-version': messages.activitiesTitleRevertedCourseVersion,
    'save-chapter-changes': messages.activitiesTitleSaveChapterChanges,
    'send-course-group-invites': messages.activitiesTitleSendCourseGroupInvites,
    'send-group-invites': messages.activitiesTitleSendGroupInvites,
    'send-path-group-invites': messages.activitiesTitleSendPathGroupInvites,
    'send-path-user-invites': messages.activitiesTitleSendPathUserInvites,
    'send-message': messages.activitiesTitleSendMessage,
    'set-new-password': messages.activitiesSetNewPasswordTitle,
    'set-new-password-admin': messages.activitiesSetNewPasswordTitle,
    'start-background-worker': messages.activitiesTitleStartBackgroundWorker,
    'start-new-course-completion':
      messages.activitiesTitleStartNewCourseCompletion,
    'submit-calendar-event-feedback':
      messages.activitiesTitleSubmitCalendarEventFeedback,
    'submit-course-completion-integration':
      messages.activitiesTitleSubmitCourseCompletionIntegration,
    'transfer-admin-rights': messages.activitiesTitleTransferAdminRights,
    'unlink-achievement-from-calendar-event':
      messages.activitiesTitleUnlinkAchievementFromCalendarEvent,
    'unlink-all-achievements-from-calendar-event':
      messages.activitiesTitleUnlinkAllAchievementsFromCalendarEvent,
    'unpin-competency-profile': messages.activitiesTitleUnpinCompetencyProfile,
    'unpublish-chapter': messages.activitiesTitleUnpublishChapter,
    'update-achievement': messages.activitiesTitleUpdateAchievement,
    'update-achievement-access-settings':
      messages.activitiesTitleUpdateAchievementAccessSettings,
    'update-achievement-admin': messages.activitiesTitleUpdateAchievementAdmin,
    'update-achievement-completions':
      messages.activitiesTitleUpdateAchievementUsers,
    'update-achievement-order-product-user-custom-attribute':
      messages.activitiesTitleUpdateProductUserCustomAttributeOrder,
    'update-achievement-product-user-custom-attribute':
      messages.activitiesTitleUpdateProductUserCustomAttribute,
    'update-achievement-template':
      messages.activitiesTitleUpdateAchievementTemplate,
    'update-achievement-completion-template':
      messages.activitiesTitleUpdateAchievementUserTemplate,
    'update-admin-group': messages.activitiesTitleUpdateAdminGroup,
    'update-answer': messages.activitiesTitleUpdateAnswer,
    'update-answer-review': messages.activitiesTitleUpdateAnswerReview,
    'update-api-routes': 'Updated external API routes',
    'update-auth-strategy': 'Updated auth strategy',
    'update-calendar-event': messages.activitiesTitleUpdateCalendarEvent,
    'update-calendar-event-access-settings':
      messages.activitiesTitleUpdateCalendarEventAccessSettings,
    'update-calendar-event-feedback-question':
      messages.activitiesTitleUpdateCalendarEventFeedbackQuestion,
    'update-calendar-event-feedback-questions-order':
      messages.activitiesTitleUpdateCalendarEventFeedbackQuestionsOrder,
    'update-calendar-event-feedback-template':
      messages.activitiesTitleUpdateCalendarEventFeedbackTemplate,
    'update-calendar-event-feedback-template-question':
      messages.activitiesTitleUpdateCalendarEventFeedbackTemplateQuestion,
    'update-calendar-event-feedback-template-questions-order':
      messages.activitiesTitleUpdateCalendarEventFeedbackTemplateQuestionsOrder,
    'update-calendar-event-order-product-user-custom-attribute':
      messages.activitiesTitleUpdateProductUserCustomAttributeOrder,
    'update-calendar-event-product-user-custom-attribute':
      messages.activitiesTitleUpdateProductUserCustomAttribute,
    'update-chapter-slug': messages.activitiesTitleUpdateChapterSlug,
    'update-chapters-order': messages.activitiesTitleUpdateChaptersOrder,
    'update-client-page-section':
      messages.activitiesTitleUpdateClientPageSection,
    'update-competency': messages.activitiesTitleUpdateCompetency,
    'update-competency-access-settings':
      messages.activitiesTitleUpdateCompetencyAccessSettings,
    'update-competency-admin': messages.activitiesTitleUpdateCompetencyAdmin,
    'update-competency-endorsement':
      messages.activitiesTitleUpdateCompetencyEndorsement,
    'update-competency-profile':
      messages.activitiesTitleUpdateCompetencyProfile,
    'update-competency-profile-access-settings':
      messages.activitiesTitleUpdateCompetencyProfileAccessSettings,
    'update-competency-profile-admin':
      messages.activitiesTitleUpdateCompetencyProfileAdmin,
    'update-course': messages.activitiesTitleUpdateCourse,
    'update-course-language-version':
      messages.activitiesTitleUpdateCourseLanguageVersion,
    'update-course-admin': messages.activitiesTitleUpdateCourseAdmin,
    'update-course-owner': messages.activitiesTitleUpdateCourseOwner,
    'update-course-publish': messages.activitiesTitleUpdateCoursePublish,
    'update-course-user-settings':
      messages.activitiesTitleUpdateCourseUserMandatoryAndTargetSchedule,
    'update-course-user-settings-for-group':
      messages.activitiesTitleUpdateCourseUserMandatoryAndTargetScheduleForGroup,
    'update-discussion-comment':
      messages.activitiesTitleUpdateDiscussionComment,
    'update-email-message-template':
      messages.activitiesTitleUpdateEmailMessageTemplate,
    'update-email-message-template-product':
      messages.activitiesTitleUpdateEmailMessageTemplateProduct,
    'update-group': messages.activitiesTitleUpdateGroup,
    'update-group-admin': messages.activitiesTitleUpdateGroupAdmin,
    'update-individual-achievement':
      messages.activitiesTitleUpdateIndividualAchievement,
    'update-integration': 'Updated integration configuration',
    'update-password': messages.activitiesTitleUpdateIndividualAchievement,
    'update-path': messages.activitiesTitleUpdatePath,
    'update-path-step': messages.activitiesTitleUpdatePathStep,
    'update-path-step-user-settings':
      messages.activitiesTitleUpdatePathStepUserSettings,
    'update-path-steps-order': messages.activitiesTitleUpdatePathStepsOrder,
    'update-profile': messages.activitiesTitleUpdateProfile,
    'update-scorm-status': messages.activitiesTitleUpdateScormStatus,
    'update-site-settings': messages.activitiesTitleUpdateSiteSettings,
    'update-supervisor': messages.activitiesTitleUpdateSupervisor,
    'update-user': messages.activitiesTitleUpdateUser,
    'update-user-ambitions': messages.activitiesTitleUpdateUserAmbitions,
    'update-user-competencies': messages.activitiesTitleUpdateUserCompetencies
  } satisfies Record<EventLogType | 'all', string>;

  if (onlyEventsOfType) {
    const eventsOfType = getLimitedEventsByTypeGroup({
      type: onlyEventsOfType
    });
    if (eventsOfType.length) {
      return Object.keys(events)
        .filter((key) => eventsOfType.includes(key))
        .reduce((obj, key) => {
          obj[key] = events[key];
          return obj;
        }, {});
    }
  }

  const disabledEventTypes = [
    ...(serverConfig.hideAchievementsFromUI
      ? getLimitedEventsByTypeGroup({ type: 'achievement' })
      : []),
    ...(serverConfig.hideCalendarEventsFromUI
      ? getLimitedEventsByTypeGroup({ type: 'calendar' })
      : []),
    ...(serverConfig.hideCoursesFromUI
      ? getLimitedEventsByTypeGroup({ type: 'course' })
      : []),
    ...(serverConfig.hidePathsFromUI
      ? getLimitedEventsByTypeGroup({ type: 'path' })
      : [])
  ];

  if (disabledEventTypes.length === 0) {
    return events;
  }

  // Filter out events related to disabled products
  const filteredEvents = Object.keys(events)
    .filter((key) => key === 'all' || !disabledEventTypes.includes(key))
    .reduce((obj, key) => {
      obj[key] = events[key];
      return obj;
    }, {});

  return filteredEvents;
};

export default getEventLogTypes;
